/*
 * @Description: 归档维护页面-第一层
 * @Author:YuKi
 * @Date: 2024/12/06 13:56:03
 */
import React, { useCallback, memo, useState, useEffect } from 'react';
import { Button, Modal, Form, Input, Table, Row, Col, Space, message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ColumnsType } from 'antd/es/table';
import { useHistory, useLocation } from 'react-router-dom';
import {
  getComponentPage,
  addComponents,
  deleteComponent,
  editComponent,
  refreshData,
} from '@/api/clientFeedback/archivalMaintenance';

/* 列表字段类型定义 */
type SecTableColumnsItem = {
  /** 行ID */
  id: string;
  /** 部件名称 */
  component: string;
  /** 创建人 */
  createAccount: string;
  /** 创建时间 */
  createDatetime: string;
  /** 部件ID */
  questionTypeId: string;
};

/* 列表查询参数 */
let searchParams = {
  /** 每页条数 */
  pageSize: 10,
  /** 当前页 */
  pageIndex: 1,
  /** 部件名称 */
  component: '',
  /** 部件ID */
  questionTypeId: '',
};
/** 前页面查询参数暂存 */
let beforeParams = {
  questionType: '',
  businessLine: '',
  pageIndexQuestion: 1,
};

const ComponentMaintenance: React.FC = () => {
  const { t } = useTranslation();
  const { confirm } = Modal;
  const history = useHistory();
  const location = useLocation();
  const [pagingRecord, setPagingRecord] = useState({ pageSize: 10, pageIndex: 1, total: 0 });
  const [dataList, setDataList] = useState<SecTableColumnsItem[]>([]);
  const [tableLoading, setTableLoading] = useState<any>(false);
  const [addOrEdit, setAddOrEdit] = useState<number>(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [addEditForm] = Form.useForm();
  const [searchForm] = Form.useForm();

  /**
   * @Author: YuKi
   * @description: 跳转至内容维护页面
   * @param {*} record
   */
  const linkToContentPage = (record) => {
    if (history.location.pathname.indexOf('/innerStatic') < 0) {
      localStorage.setItem('pathname', history.location.pathname);
    }
    history.push({
      pathname: '/innerStatic/MoudleMaintenance/index',
      state: {
        componentId: record.id,
        pageIndexComponent: pagingRecord.pageIndex,
        pageIndexQuestion: beforeParams.pageIndexQuestion,
        component: searchParams.component,
        businessLine: beforeParams.businessLine,
        questionTypeId: searchParams.questionTypeId,
        questionType: beforeParams.questionType,
      },
    });
  };

  /**
   * @Author: YuKi
   * @description: 返回前一个页面
   */
  const backTo = () => {
    history.push({
      pathname: '/maintenanceManagement/archivalMaintenance',
      state: {
        pageIndex: beforeParams.pageIndexQuestion,
        questionType: beforeParams.questionType,
        businessLine: beforeParams.businessLine,
      },
    });
  };

  /**
   * @Author: YuKi
   * @description: 获取模块分页列表
   * @return {*}
   */
  const getComponent = () => {
    setTableLoading(true);
    getComponentPage(searchParams)
      .then((rep: any) => {
        if (rep && rep.code === 200 && rep.data && rep.data.rows) {
          setDataList([...rep.data.rows]);
          let paging = {
            pageIndex: rep.data.pageIndex,
            pageSize: rep.data.pageSize,
            total: rep.data.total,
          };
          setPagingRecord({ ...paging });
        } else {
          setDataList([]);
          let pagingInit = {
            pageIndex: 1,
            pageSize: 10,
            total: 0,
          };
          setPagingRecord({ ...pagingInit });
        }
        setTableLoading(false);
      })
      .catch((err) => {
        setDataList([]);
        let pagingInit = {
          pageIndex: 1,
          pageSize: 10,
          total: 0,
        };
        setPagingRecord({ ...pagingInit });
        setTableLoading(false);
      });
  };

  /**
   * @Author: YuKi
   * @description: 新增或编辑模块
   */
  const addOrEditComponents = () => {
    if (addOrEdit === 1) {
      addEditForm.validateFields().then((values) => {
        addComponents({ ...values }).then((res: any) => {
          if (res.code == 200) {
            message.success(t('complaintList.operateSuccess'));
            getComponent();
            handleCancel();
          }
        });
      });
    } else {
      addEditForm.validateFields().then((values) => {
        editComponent({ ...values }).then((res: any) => {
          if (res.code == 200) {
            message.success(t('complaintList.operateSuccess'));
            getComponent();
            handleCancel();
          }
        });
      });
    }
  };

  useEffect(() => {
    if (location.state && location.state.questionTypeId) {
      searchParams.questionTypeId = location.state.questionTypeId;
      // 参数暂存
      beforeParams.questionType = location.state.questionType;
      beforeParams.businessLine = location.state.businessLine;
      searchParams.pageIndex = location.state.pageIndex ?? 1;
      beforeParams.pageIndexQuestion = location.state.pageIndexQuestion;
      beforeParams.pageIndexQuestion = location.state.pageIndexQuestion;
      searchParams.component = location.state.component;
      searchForm.setFieldValue('component', location.state.component);
    }

    getComponent();
    return () => {
      searchParams = {
        pageSize: 10,
        pageIndex: 1,
        component: '',
        questionTypeId: '',
      };
    };
  }, []);

  /**
   * @Author: YuKi
   * @description: 分页回调函数
   * @param {*} useCallback
   */
  const pageChange = useCallback((currentPage, currentSize) => {
    searchParams = { ...searchParams, ...searchForm.getFieldsValue() };
    searchParams.pageIndex = currentPage === undefined ? searchParams.pageIndex : currentPage;
    searchParams.pageSize = currentSize === undefined ? searchParams.pageSize : currentSize;
    getComponent();
  }, []);

  /**
   * @Author: YuKi
   * @description: 按钮查询分页列表
   */
  const search = () => {
    searchParams = { ...searchParams, ...searchForm.getFieldsValue() };
    searchParams.pageIndex = 1;
    searchParams.pageSize = 10;
    getComponent();
  };

  /**
   * @Author: YuKi
   * @description: 删除选中的行
   * @param {*} id
   */
  const handleDelete = (id) => {
    confirm({
      title: t('common.tip'),
      closable: true,
      icon: <ExclamationCircleOutlined />,
      content: t('common.deleteData'),
      okText: t('common.confirmBtn'),
      cancelText: t('common.cancelBtn'),
      onOk() {
        deleteComponent({ id: id }).then((rep: any) => {
          if (rep && rep.code === 200) {
            message.success(t('common.deleteSuccess'));
            getComponent();
          }
        });
      },
      onCancel() {
        message.info(t('common.operateCancel'));
      },
    });
  };

  /**
   * @Author: YuKi
   * @description: 打开新增或编辑弹窗
   * @param {number} type 弹窗类型 1:新增 2:编辑
   * @param {any} record
   */
  const displayAddOrEditDialog = (type: number, record?: any) => {
    setIsModalOpen(true);
    if (type === 1) {
      setAddOrEdit(1);
      addEditForm && addEditForm.resetFields();
      addEditForm.setFieldValue('questionTypeId', searchParams.questionTypeId);
    } else {
      setAddOrEdit(2);
      addEditForm &&
        addEditForm.setFieldsValue({
          id: record.id,
          component: record.component,
          businessLine: record.businessLine,
          questionTypeId: record.questionTypeId,
        });
    }
  };

  /**
   * @Author: YuKi
   * @description: 关闭弹窗
   */
  const handleCancel = () => {
    if (addEditForm) {
      addEditForm.resetFields();
    }
    setIsModalOpen(false);
  };

  /**
   * @Author: YuKi
   * @description: 同步历史数据(全部)
   */
  const refreshAll = () => {
    refreshData({}).then((rep: any) => {
      if (rep.code == 200) {
        message.success(t('complaintList.operateSuccess'));
      }
    });
  };

  /**
   * @Author: YuKi
   * @description: 同步历史数据(单个)
   * @param {*} id
   */
  const refresh = (id) => {
    refreshData({ componentId: id }).then((rep: any) => {
      if (rep.code == 200) {
        message.success(t('complaintList.operateSuccess'));
      }
    });
  };

  /* 列表字段 */
  const columns: ColumnsType<SecTableColumnsItem> = [
    /* 序号 */
    {
      title: t('common.serialNumber'),
      width: 140,
      align: 'center',
      render: (text: any, record: any, index: any) => {
        return index + (pagingRecord.pageIndex - 1) * pagingRecord.pageSize + 1;
      },
    },
    /* 部件名称 */
    {
      title: t('archivalMaintenance.component'),
      dataIndex: 'component',
      width: 180,
      align: 'center',
      ellipsis: true,
    },
    /* 创建人 */
    {
      title: t('common.creator'),
      dataIndex: 'createAccount',
      width: 160,
      align: 'center',
      ellipsis: true,
    },
    /* 创建时间 */
    {
      title: t('common.createTime'),
      dataIndex: 'createServerDatetime',
      width: 160,
      align: 'center',
      ellipsis: true,
    },
    /* 操作 */
    {
      title: t('common.operation'),
      align: 'center',
      width: 340,
      render: (text: any, record: any, index: any) => {
        return (
          <Space size="middle" wrap className="sn-management-space">
            {/* 编辑 */}
            <a onClick={() => displayAddOrEditDialog(2, record)}>{t('common.editBtn')}</a>
            {/* 内容维护 */}
            <a
              onClick={() => {
                linkToContentPage(record);
              }}
            >
              {t('archivalMaintenance.contentMaintenance')}
            </a>
            {/* 刷新 */}
            <a
              onClick={() => {
                refresh(record.id);
              }}
            >
              {t('complaintList.refresh')}
            </a>
            {/* 删除 */}
            <a
              className="delete-text"
              onClick={() => {
                handleDelete(record.id);
              }}
            >
              {t('common.deleteBtn')}
            </a>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <div className="custom-contant project-management">
        <Form
          form={searchForm}
          autoComplete="off"
          style={{ width: '100%' }}
          onFinish={search}
          className="custom-search in-line-style"
          layout="vertical"
        >
          <Row gutter={12} align={'bottom'}>
            {/* 部件名称 */}
            <Col xs={24} sm={8} md={8} lg={6} xl={6}>
              <Form.Item label={t('archivalMaintenance.component')} name="component">
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24} lg={18} xl={18}>
              <Row>
                <Col xs={16} sm={16} md={16} lg={16} xl={16}>
                  <Form.Item>
                    <Space size="middle">
                      <Button type="primary" shape="round" htmlType="submit">
                        {t('common.searchBtn')}
                      </Button>
                      <Button type="primary" shape="round" onClick={() => displayAddOrEditDialog(1)}>
                        {t('common.addBtn')}
                      </Button>
                      <Button type="primary" shape="round" onClick={() => refreshAll()}>
                        {t('complaintList.refresh')}
                      </Button>
                    </Space>
                  </Form.Item>
                </Col>
                <Col xs={8} sm={8} md={8} lg={8} xl={8} style={{ textAlign: 'right' }}>
                  <Button type="primary" shape="round" onClick={backTo}>
                    {t('common.goback')}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>

        {/* 表格 */}
        <Table
          columns={columns}
          loading={tableLoading}
          rowKey={'id'}
          pagination={{
            showQuickJumper: true,
            showSizeChanger: false,
            defaultCurrent: 1,
            current: pagingRecord.pageIndex,
            total: pagingRecord.total,
            pageSize: pagingRecord.pageSize,
            onChange: pageChange,
          }}
          dataSource={dataList}
          scroll={{ x: 600, y: window.innerHeight - 350 }}
          className="custom-table-flex"
        />
      </div>

      {/* 新增/编辑弹窗 */}
      <Modal
        maskClosable={false}
        title={addOrEdit === 1 ? t('common.addBtn') : t('common.editBtn')}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={[
          <Button shape="round" onClick={handleCancel}>
            {t('common.cancelBtn')}
          </Button>,
          <Button shape="round" type="primary" onClick={addOrEditComponents}>
            {t('common.saveBtn')}
          </Button>,
        ]}
        forceRender
      >
        <Form autoComplete="off" layout="vertical" form={addEditForm}>
          {/* 部件名称 */}
          <Row gutter={12}>
            <Col span={24}>
              <Form.Item
                label={t('archivalMaintenance.component')}
                name="component"
                rules={[
                  {
                    required: true,
                    message: `${t('archivalMaintenance.component')}${t('archivalMaintenance.parmasRequired')}`,
                    whitespace: true,
                  },
                  {
                    validator: (_, value) => {
                      if (value && value.trim() && value.trim().length > 64) {
                        return Promise.reject(t('common.maxlengLimit', { maxLength: 64 }));
                      } else {
                        return Promise.resolve();
                      }
                    },
                  },
                ]}
              >
                <Input allowClear />
              </Form.Item>
            </Col>
            {addOrEdit === 2 ? (
              <Col span={24}>
                {/* 部件ID */}
                <Form.Item label={'部件ID'} name="id" hidden={true}>
                  <Input disabled />
                </Form.Item>
              </Col>
            ) : null}

            <Col span={24}>
              {/* 问题ID */}
              <Form.Item label={'问题ID'} name="questionTypeId" hidden={true}>
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default memo(ComponentMaintenance);
